"use client";

import { useRouter } from "@/infrastructure/router/navigation";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
	ArtistIcon,
	GlassButton,
	GlassCard,
	NavigationBar,
} from "@/components/design-system/ArtistDesignSystem";
import { useTranslation } from "@/i18n/ArtistI18n";

const SETTINGS_PREFERENCES_KEY = "artist.preferences.settings.v2";
const PLAYER_LEVER_KEY = "artist.player.lever.v1";

type LeverStyle = "circle" | "diamond" | "music" | "sparkles" | "star" | "emoji";
type AudioQuality = "auto" | "high" | "lossless" | "hiRes";

interface PlayerLeverPreference {
	style: LeverStyle;
	emoji: string;
}

interface BrowserPermissionState {
	microphone: "idle" | "granted" | "denied";
	camera: "idle" | "granted" | "denied";
	notifications: NotificationPermission | "unsupported";
}

function readJson<T>(key: string, fallback: T): T {
	try {
		const raw = window.localStorage.getItem(key);
		return raw === null ? fallback : (JSON.parse(raw) as T);
	} catch {
		return fallback;
	}
}

function SettingsDetailShell({
	title,
	children,
}: Readonly<{ title: string; children: ReactNode }>) {
	return (
		<section className="settings-detail native-page" aria-label={title}>
			<NavigationBar title={title} backHref="/settings" />
			<div className="settings-detail__content">{children}</div>
		</section>
	);
}

export function PlayerSettingsDetail() {
	const { t } = useTranslation();
	const [preference, setPreference] = useState<PlayerLeverPreference>({
		style: "circle",
		emoji: "✨",
	});

	useEffect(() => {
		setPreference(
			readJson<PlayerLeverPreference>(PLAYER_LEVER_KEY, {
				style: "circle",
				emoji: "✨",
			}),
		);
	}, []);

	function update(next: PlayerLeverPreference): void {
		setPreference(next);
		window.localStorage.setItem(PLAYER_LEVER_KEY, JSON.stringify(next));
	}

	const styles: ReadonlyArray<{ value: LeverStyle; label: string; glyph: string }> = [
		{ value: "circle", label: t("settings.player.circle"), glyph: "●" },
		{ value: "diamond", label: t("settings.player.diamond"), glyph: "◆" },
		{ value: "music", label: t("settings.player.music"), glyph: "♪" },
		{ value: "sparkles", label: t("settings.player.sparkles"), glyph: "✦" },
		{ value: "star", label: t("settings.player.star"), glyph: "★" },
		{ value: "emoji", label: t("settings.player.emoji"), glyph: preference.emoji || "✨" },
	];

	const selectedGlyph = styles.find((item) => item.value === preference.style)?.glyph ?? "●";

	return (
		<SettingsDetailShell title={t("settings.player.title")}>
			<GlassCard className="settings-detail__preview">
				<div className="settings-lever" aria-hidden="true">
					<span className="settings-lever__track" />
					<span className="settings-lever__thumb">{selectedGlyph}</span>
				</div>
				<div>
					<strong>{t("settings.player.preview")}</strong>
					<p>{t("settings.player.description")}</p>
				</div>
			</GlassCard>

			<fieldset className="settings-option-grid">
				<legend className="sr-only">{t("settings.player.style")}</legend>
				{styles.map((item) => {
					const selected = item.value === preference.style;
					return (
						<label
							key={item.value}
							className="settings-option-card"
							data-selected={selected}
						>
							<input
								className="sr-only"
								type="radio"
								name="player-lever-style"
								value={item.value}
								checked={selected}
								onChange={() => update({ ...preference, style: item.value })}
							/>
							<span className="settings-option-card__glyph">{item.glyph}</span>
							<strong>{item.label}</strong>
							<ArtistIcon name={selected ? "check" : "circle"} size={18} />
						</label>
					);
				})}
			</fieldset>

			<GlassCard className="settings-detail__form">
				<label htmlFor="player-lever-emoji">{t("settings.player.emojiLabel")}</label>
				<input
					id="player-lever-emoji"
					value={preference.emoji}
					maxLength={8}
					onChange={(event) => update({ ...preference, emoji: event.target.value })}
				/>
			</GlassCard>
		</SettingsDetailShell>
	);
}

export function AudioQualitySettingsDetail() {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<AudioQuality>("auto");

	useEffect(() => {
		const stored = readJson<Record<string, unknown>>(SETTINGS_PREFERENCES_KEY, {});
		const quality = stored.audioQuality;
		if (quality === "auto" || quality === "high" || quality === "lossless" || quality === "hiRes") {
			setSelected(quality);
		}
	}, []);

	function select(value: AudioQuality): void {
		setSelected(value);
		const stored = readJson<Record<string, unknown>>(SETTINGS_PREFERENCES_KEY, {});
		window.localStorage.setItem(
			SETTINGS_PREFERENCES_KEY,
			JSON.stringify({ ...stored, audioQuality: value }),
		);
	}

	const options: ReadonlyArray<{ value: AudioQuality; title: string; subtitle: string }> = [
		{ value: "auto", title: t("settings.audio.auto"), subtitle: t("settings.audio.autoDescription") },
		{ value: "high", title: t("settings.audio.high"), subtitle: t("settings.audio.highDescription") },
		{ value: "lossless", title: t("settings.audio.lossless"), subtitle: t("settings.audio.losslessDescription") },
		{ value: "hiRes", title: t("settings.audio.hiRes"), subtitle: t("settings.audio.hiResDescription") },
	];

	return (
		<SettingsDetailShell title={t("settings.audio.title")}>
			<GlassCard className="settings-detail__callout">
				<ArtistIcon name="info" />
				<p>{t("settings.audio.note")}</p>
			</GlassCard>
			<GlassCard className="settings-detail__list">
				{options.map((option) => (
					<button
						key={option.value}
						type="button"
						className="settings-detail__choice"
						data-selected={selected === option.value}
						onClick={() => select(option.value)}
					>
						<ArtistIcon name="waveform" />
						<span><strong>{option.title}</strong><small>{option.subtitle}</small></span>
						<ArtistIcon name={selected === option.value ? "check" : "circle"} />
					</button>
				))}
			</GlassCard>
			<div className="settings-audio-quality-footer">
				{options.map((option) => (
					<p key={`footer-${option.value}`}>
						<strong>{option.title}</strong>: {option.subtitle}
					</p>
				))}
			</div>
		</SettingsDetailShell>
	);
}

export function BrowserPermissionsSettingsDetail() {
	const { t } = useTranslation();
	const [state, setState] = useState<BrowserPermissionState>({
		microphone: "idle",
		camera: "idle",
		notifications: typeof Notification === "undefined" ? "unsupported" : Notification.permission,
	});

	async function requestMedia(kind: "microphone" | "camera"): Promise<void> {
		try {
			const stream = await navigator.mediaDevices.getUserMedia(
				kind === "microphone" ? { audio: true } : { video: true },
			);
			stream.getTracks().forEach((track) => {
				track.stop();
			});
			setState((current) => ({ ...current, [kind]: "granted" }));
		} catch {
			setState((current) => ({ ...current, [kind]: "denied" }));
		}
	}

	async function requestNotifications(): Promise<void> {
		if (typeof Notification === "undefined") return;
		const permission = await Notification.requestPermission();
		setState((current) => ({ ...current, notifications: permission }));
	}

	return (
		<SettingsDetailShell title={t("settings.permissions.title")}>
			<GlassCard className="settings-detail__list">
				<PermissionRow title={t("settings.permissions.microphone")} state={state.microphone} onRequest={() => void requestMedia("microphone")} />
				<PermissionRow title={t("settings.permissions.camera")} state={state.camera} onRequest={() => void requestMedia("camera")} />
				<PermissionRow title={t("settings.permissions.notifications")} state={state.notifications} onRequest={() => void requestNotifications()} />
			</GlassCard>
			<p className="settings-detail__footnote">{t("settings.permissions.browserNote")}</p>
		</SettingsDetailShell>
	);
}

function PermissionRow({
	title,
	state,
	onRequest,
}: Readonly<{
	title: string;
	state: "idle" | "granted" | "denied" | "default" | "unsupported";
	onRequest: () => void;
}>) {
	const { t } = useTranslation();
	return (
		<div className="settings-permission-row">
			<span><strong>{title}</strong><small>{t(`settings.permissions.state.${state}`)}</small></span>
			<GlassButton size="sm" kind="secondary" onClick={onRequest}>
				{t("settings.permissions.request")}
			</GlassButton>
		</div>
	);
}

export function DevicePlaybackSettingsDetail() {
	const { t } = useTranslation();
	const router = useRouter();
	const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([]);
	const mediaSessionSupported = useMemo(
		() => typeof navigator !== "undefined" && "mediaSession" in navigator,
		[],
	);

	useEffect(() => {
		if (!navigator.mediaDevices?.enumerateDevices) return;
		navigator.mediaDevices.enumerateDevices().then((devices) => {
			setOutputs(devices.filter((device) => device.kind === "audiooutput"));
		}).catch(() => setOutputs([]));
	}, []);

	return (
		<SettingsDetailShell title={t("settings.devicePlayback.title")}>
			<GlassCard className="settings-detail__callout">
				<ArtistIcon name="play" />
				<div>
					<strong>{t("settings.devicePlayback.browser")}</strong>
					<p>{mediaSessionSupported ? t("settings.devicePlayback.supported") : t("settings.devicePlayback.unsupported")}</p>
				</div>
			</GlassCard>
			<GlassCard className="settings-detail__list">
				{outputs.length === 0 ? (
					<p className="settings-detail__empty">{t("settings.devicePlayback.noOutputs")}</p>
				) : outputs.map((device, index) => (
					<div className="settings-permission-row" key={device.deviceId || String(index)}>
						<span><strong>{device.label || t("settings.devicePlayback.outputFallback")}</strong><small>{t("settings.devicePlayback.output")}</small></span>
					</div>
				))}
			</GlassCard>
			<GlassButton kind="primary" expands onClick={() => router.push("/player")}>
				{t("settings.devicePlayback.openPlayer")}
			</GlassButton>
		</SettingsDetailShell>
	);
}
